<template>
  <div class="v2026-im">
    <!-- The head line always renders, even with no value — otherwise the first one cannot be added. -->
    <div class="v2026-im__section-head">
      <span class="v2026-im__section-label">ข้อความสำคัญ</span>
      <template v-if="customer">
        <button v-if="value" type="button" class="v2026-im__icon-btn"
                @click="startEdit" aria-label="แก้ไขข้อความสำคัญ">
          <img class="v2026-im__pencil" aria-hidden="true" :src="editIcon" alt="">
        </button>
        <button v-else type="button" class="v2026-im__add"
                @click="startEdit" aria-label="เพิ่มข้อความสำคัญ">
          <span class="v2026-im__add-ico" aria-hidden="true" :style="maskStyle(plusIcon)"></span>
          <span class="v2026-im__add-text">เพิ่ม</span>
        </button>
      </template>
    </div>

    <div v-if="value" class="v2026-im__notebox">{{ value }}</div>

    <BaseModal :open="isEditing" max-width="700px" :position="['top', 'center']"
               :modalStyle="['margin-top:10%']" @close="isEditing = false">
      <div class="v2026-im__edit">
        <div class="v2026-im__edit-head">
          <span class="v2026-im__edit-title">ข้อความสำคัญ</span>
          <button type="button" class="v2026-im__edit-close" aria-label="ปิด"
                  @click="isEditing = false">×</button>
        </div>
        <div class="v2026-im__edit-body">
          <label class="v2026-im__edit-label">ข้อความสำคัญ</label>
          <div class="v2026-im__edit-row">
            <div class="v2026-im__edit-field">
              <textarea class="v2026-im__edit-ta" rows="3" :maxlength="importanNoteMaxlength"
                        v-model="draft"
                        placeholder="พิมพ์ Note สำคัญ (ไม่เกิน 1 ประโยค)…"></textarea>
              <span class="v2026-im__edit-counter">{{ (draft || '').length }}/{{ importanNoteMaxlength }}</span>
            </div>
            <button type="button" class="v2026-im__edit-save"
                    :disabled="saving" @click="save">
              {{ saving ? 'กำลังบันทึก…' : 'บันทึก' }}
            </button>
          </div>
          <p class="v2026-im__edit-hint">* กรอกข้อความเพื่อให้ Admin คนอื่นอ่านได้ง่าย</p>
          <div class="v2026-im__edit-warn">
            <span class="v2026-im__edit-warn-ico" aria-hidden="true">⚠️</span>
            Note สำคัญสั้นๆ เพื่อบอกสิ่งสำคัญ
          </div>
        </div>
      </div>
    </BaseModal>

    <BaseToast :open="toast.open" :variant="toast.variant" :title="toast.title" :message="toast.message"
               @close="toast.open = false" />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import BaseModal from '@/components/coversation/BaseModal.vue'
import BaseToast from '@/components/coversation/BaseToast.vue'

export default {
  name: 'important-message-v2026',
  components: {
    BaseModal,
    BaseToast
  },
  props: {
    customer: {
      type: Object,
      default: null
    },
    conversationId: {
      type: [Number, String],
      default: null
    }
  },
  data () {
    return {
      isEditing: false, // edit BaseModal visibility
      draft: '', // edit-mode textarea buffer
      // saveCustomer does not commit to the store, so the just-saved text is held here.
      override: undefined,
      saving: false, // PUT in flight → disable save
      toast: { open: false, variant: 'success', title: '', message: '' },
      // Stroke-based pencil rendered as an <img>, NOT a CSS mask — a mask paints it as a solid square.
      editIcon: require('@/assets/icons2026/pencil-edit.svg'),
      plusIcon: require('@/assets/icons2026/plus.svg')
    }
  },
  computed: {
    value () {
      if (this.override !== undefined) return this.override
      return (this.customer && this.customer.important_message) || ''
    },
    importanNoteMaxlength () {
      return 2000
    }
  },
  watch: {
    // Drop the local override on a conversation change, or the previous chat's value leaks through.
    conversationId () {
      this.override = undefined
      this.isEditing = false
    }
  },
  methods: {
    ...mapActions('customer', ['saveCustomer']),
    startEdit () {
      this.draft = this.value
      this.isEditing = true
    },
    async save () {
      if (!this.customer || !this.customer.id) return
      const trimmed = (this.draft || '').trim()
      this.saving = true
      try {
        await this.saveCustomer({
          customer: { id: this.customer.id, important_message: trimmed },
          conversation_id: this.conversationId
        })
        this.override = trimmed
        if (this.customer) this.customer.important_message = trimmed
        this.isEditing = false
        this.showToast('success', 'บันทึกข้อมูลสำเร็จ', 'ทำการบันทึกข้อมูลสำเร็จ')
      } catch (e) {
        this.showToast('danger', 'เกิดข้อผิดพลาด', 'บันทึกไม่สำเร็จ กรุณาลองใหม่')
      } finally {
        this.saving = false
      }
    },
    showToast (variant, title, message) {
      this.toast = { open: true, variant, title, message }
    },
    maskStyle (url) {
      return {
        '-webkit-mask-image': 'url(' + url + ')',
        'mask-image': 'url(' + url + ')'
      }
    }
  }
}
</script>

<style scoped>
.v2026-im {
  --v2026-im-text: #101828;
  --v2026-im-text-secondary: #667085;
  --v2026-im-surface-sunken: #F8FAFC;
  --v2026-im-surface-hover: #e9ecf1;
  --v2026-im-main: #31C1D7;

  padding: 0 16px;
}

.v2026-im__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.v2026-im__section-label {
  font-weight: 400;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0;
  color: #66696C;
}

.v2026-im__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
}
.v2026-im__icon-btn:hover {
  background: var(--v2026-im-surface-hover);
}
.v2026-im__icon-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-im__pencil {
  width: 18px;
  height: 18px;
  object-fit: contain;
  display: block;
}

.v2026-im__add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px 0 6px;
  border: 0;
  background: transparent;
  border-radius: 999px;
  color: var(--v2026-im-main);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.v2026-im__add:hover {
  background: rgba(49, 193, 215, 0.12);
}
.v2026-im__add:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-im__add-ico {
  display: inline-block;
  width: 14px;
  height: 14px;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: currentColor;
}

.v2026-im__notebox {
  margin-top: 8px;
  min-height: 70px;
  background: var(--v2026-im-surface-sunken);
  border-radius: 8px;
  padding: 12px 16px;
  font-weight: 700;
  color: var(--v2026-im-text);
  font-size: 14px;
  line-height: 1.5;

  white-space: pre-line;
}

.v2026-im__edit-head {
  display: flex;
  align-items: center;
  justify-content: space-between;

  background: #FFFFFF;
  border-bottom: 1px solid #EBEDF0;
  padding: 16px 24px;
}
.v2026-im__edit-body {
  padding: 20px 24px;
}
.v2026-im__edit-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--v2026-im-text);
}
.v2026-im__edit-close {
  border: none;
  background: none;
  padding: 0;
  font-size: 20px;
  line-height: 1;
  color: #66696C;
  cursor: pointer;
}
.v2026-im__edit-label {
  display: block;
  margin: 0 0 6px;
  font-size: 14px;
  color: #66696C;
}
.v2026-im__edit-row {
  display: flex;
  gap: 12px;
  align-items: stretch;
}
.v2026-im__edit-field {
  position: relative;
  flex: 1;
}
.v2026-im__edit-ta {
  width: 100%;
  min-height: 44px;
  /* resize: none; */
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 10px 56px 10px 12px;
  font-size: 14px;
}
.v2026-im__edit-ta:focus {
  outline: none;
  border-color: #31C1D7;
}
.v2026-im__edit-counter {
  position: absolute;
  right: 12px;
  bottom: 8px;
  font-size: 13px;
  color: #9CA3AF;
  pointer-events: none;
}
.v2026-im__edit-save {
  height: 44px;
  padding: 0 20px;
  background: #31C1D7;
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.v2026-im__edit-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.v2026-im__edit-hint {
  margin-top: 8px;
  margin-bottom: 0;
  font-size: 12px;
  color: #9CA3AF;
}
.v2026-im__edit-warn {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #FEF9C3;
  color: #92400E;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
}
</style>
